import React, { useState, useEffect } from 'react';
import CustomModal from './CustomModal';

// This component renders a scorecard for a golf course, allowing users to enter scores for each hole.
function Scorecard({ course, holes, onSave, isLastCourse, handleCompleteEvent, eventId }) {
  const [currentHoleIndex, setCurrentHoleIndex] = useState(0);
  const [scores, setScores] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalContent, setModalContent] = useState({ title: '', message: '', onConfirm: null });
  const [currentPage, setCurrentPage] = useState(0); // 0: front 9, 1: back 9

  useEffect(() => {
    setScores(Array(holes.length).fill(null));
    setCurrentPage(0); // Reset to front 9 when holes change
  }, [holes]);

  const handleScoreChange = (index, value) => {
    const newScores = [...scores];
    newScores[index] = value === "" ? null : parseInt(value, 10);
    setScores(newScores);
  };  

  // Handle moving to the next set of holes, ending the round, or completing the event
  const handleNext = () => {
    if (document.activeElement) document.activeElement.blur();
    const allScoresEntered = scores.every((score) => score !== null);
    const noZeroScores = scores.every((score) => score !== 0);

    if (!allScoresEntered) {
      setModalContent({
        title: 'Validation Error',
        message: 'Please enter a valid score for all holes before proceeding.',
        onConfirm: () => setModalOpen(false),
      });
      setModalOpen(true);
      return;
    }

    if (!noZeroScores) {
      setModalContent({
        title: 'Validation Error',
        message: 'Scores cannot have a value of 0. Please correct your input.',
        onConfirm: () => setModalOpen(false),
      });
      setModalOpen(true);
      return;
    }

    const scoresToSave = scores.map((score, index) => ({
      par: holes[index].par,
      shots: score,
    }));
    const totalShots = scores.reduce((sum, s) => sum + s, 0);

    let message = isLastCourse
      ? `You took ${totalShots} shots. Submit and complete the event?`
      : `You took ${totalShots} shots. Submit and end the round?`;

    setModalContent({
      title: 'Confirm Scores',
      message,
      onConfirm: () => {
        setModalOpen(false);
        onSave(scoresToSave, true);
        if (isLastCourse) {
          handleCompleteEvent(eventId);
        }
      }
    });
    setModalOpen(true);
  };

  const buttonText = isLastCourse ? 'Complete Event' : 'End Round';

  // Calculate which holes to show
  const holesPerPage = 9;
  const totalPages = Math.ceil(holes.length / holesPerPage);
  const startIdx = currentPage * holesPerPage;
  const endIdx = startIdx + holesPerPage;
  const visibleHoles = holes.slice(startIdx, endIdx);
  const visibleScores = scores.slice(startIdx, endIdx);

  if (!holes || holes.length === 0) return <div id="loading-holes" className="loading">Loading holes...</div>;

  return (
    <div id="scorecard" className="scorecard">
      <h3 id="course-title">{course.name}</h3>
      <table id="scorecard-table" className="table">
        <thead>
          <tr>
            <th>Hole</th>
            <th>Par</th>
            <th>Score</th>
          </tr>
        </thead>
        <tbody>
          {visibleHoles.map((hole, index) => (
            <tr key={hole.id}>
              <td>{hole.hole_number}</td>
              <td>{hole.par}</td>
              <td>
                <input
                  type="number"
                  className="score-input"
                  value={visibleScores[index] === null ? "" : visibleScores[index]}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === "" || /^[0-9]+$/.test(value)) {
                      handleScoreChange(startIdx + index, value);
                    }
                  }}
                  onKeyDown={(e) => {
                    if (
                      e.key === "-" ||
                      e.key === "e" ||
                      e.key === "+" ||
                      e.key === "." ||
                      e.key === "," ||
                      e.key === " " ||
                      (isNaN(Number(e.key)) && e.key !== "Backspace" && e.key !== "Tab")
                    ) {
                      e.preventDefault();
                    }
                  }}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div>
        {currentPage > 0 && (
          <button
            className="scorecard-button"
            style={{
              backgroundColor: '#00387d',
              color: '#fff',
              borderRadius: '15px',
              border: 'none',
              padding: '8px 20px',
              margin: '5px',
              cursor: 'pointer'
            }}
            onClick={() => setCurrentPage(currentPage - 1)}
          >
            Previous 9
          </button>
        )}
        {currentPage < totalPages - 1 && (
          <button
            className="scorecard-button"
            style={{
              backgroundColor: '#00387d',
              color: '#fff',
              borderRadius: '15px',
              border: 'none',
              padding: '8px 20px',
              margin: '5px',
              cursor: 'pointer'
            }}
            onClick={() => setCurrentPage(currentPage + 1)}
          >
            Next 9
          </button>
        )}
      </div>
      {/* Only show the submit button on the last page, or always for 9-hole courses */}
      {(currentPage === totalPages - 1) && (
        <button id="next-button" className="button scorecard-button" onClick={handleNext}>
          {buttonText}
        </button>
      )}
      <CustomModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        title={modalContent.title}
        message={modalContent.message}
        onConfirm={modalContent.onConfirm}
      />
    </div>
  );
}

export default Scorecard;